import { type } from 'arktype';
import {
  calendarSlotSchema,
  calendarSlotUpdateSchema,
  updateCalendarSchema,
  slotOnlineMeetingSchema,
  type CalendarSlot,
} from './calendar.model';
import { slotOverlapConflictSchema, type SlotOverlapConflict } from './slot-overlap.model';
import { calendarSlotDataSchema, calendarSlotPartialDataSchema } from './calendar-slot-data.model';
import { meta, stringField } from './schema-doc';

const dateKeyPattern = /^\d{4}-\d{2}-\d{2}$/;
const isRealCalendarDate = (value: string) => {
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
};
const dateKeySchema = type(dateKeyPattern).narrow((value, ctx) => isRealCalendarDate(value) || ctx.mustBe('a real calendar date'));
const dateKeyField = (description: string, examples: string[]) => dateKeySchema.configure(meta(description, examples));

const dateFromField = dateKeyField('Inclusive start date (YYYY-MM-DD).', ['2026-04-22']);
const dateToField = dateKeySchema
  .or('null')
  .configure(
    meta(
      'Inclusive end date (YYYY-MM-DD), on or after `dateFrom`. `null` or omitted means a single-day slot; `null` on update clears it.',
      ['2026-04-26']
    )
  );
const recurrenceRuleField = type('string|null').configure(
  meta(
    'iCal RRULE. `null` or omitted means non-recurring; `null` on update clears it. Rejected with 400 `SLOT_TYPE_RECURRENCE_UNSUPPORTED` when the slot type sets `behaviorFlags.supportsRecurrence: false`.',
    ['FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR']
  )
);

export const calendarSlotWriteSchema = calendarSlotUpdateSchema.merge({
  'data?': calendarSlotDataSchema,
});

/** Documents the PATCH body with typed slot writes. Server validation still runs on `updateCalendarSchema`. */
export const updateCalendarBodyDocSchema = updateCalendarSchema.merge({
  'slots?': type({ '[string]': calendarSlotWriteSchema }).or('null'),
});

export const createCalendarSlotSchema = type({
  dateFrom: dateFromField,
  'dateTo?': dateToField,
  'recurrenceRule?': recurrenceRuleField,
  data: calendarSlotDataSchema,
}).configure({ description: 'Payload for creating one calendar slot.' });
export type CreateCalendarSlot = typeof createCalendarSlotSchema.t;

export const updateCalendarSlotSchema = type({
  'dateFrom?': dateFromField,
  'dateTo?': dateToField,
  'recurrenceRule?': recurrenceRuleField,
  'data?': calendarSlotPartialDataSchema,
}).configure({
  description:
    'Partial update of one calendar slot. Omitted fields stay unchanged, `data` is partial too (omitted data fields and custom property keys stay, `null` for a custom property key removes it), an empty body is a no-op.',
});
export type UpdateCalendarSlot = typeof updateCalendarSlotSchema.t;

export const calendarSlotResourceSchema = calendarSlotSchema
  .merge({
    id: type('string.uuid').configure(meta('Slot id. For an expanded recurring occurrence this is the id of the series master.')),
    'occurrenceDate?': dateKeyField('Only on expanded recurring occurrences: the date of this occurrence.', ['2026-05-08']),
    dateFrom: dateFromField,
    dateTo: dateToField,
    onlineMeeting: slotOnlineMeetingSchema
      .or('null')
      .configure(meta('Read-only: Teams join link of the verified Outlook meeting, else `null`.')),
    'createdAt?': stringField('Read-only: ISO 8601 creation timestamp, stamped by the server.'),
    'createdBy?': type({ id: 'string', name: 'string' })
      .or('null')
      .configure(meta('Read-only: user that created the slot, stamped by the server.')),
  })
  .configure({ description: 'One calendar slot.' });
export interface CalendarSlotResource extends CalendarSlot {
  id: string;
  occurrenceDate?: string;
}

export const calendarSlotWriteResultSchema = calendarSlotResourceSchema.and({
  'warnings?': slotOverlapConflictSchema.array().configure(meta('Overlaps with `warn` policy caused by this write.')),
});
export type CalendarSlotWriteResult = CalendarSlotResource & { warnings?: SlotOverlapConflict[] };

export const listCalendarSlotsQuerySchema = type({
  from: dateKeyField('First date of the slot range (YYYY-MM-DD, inclusive).', ['2026-04-01']),
  to: dateKeyField('Last date of the slot range (YYYY-MM-DD, inclusive).', ['2026-04-30']),
}).configure({ description: 'Query parameters for listing the slots of a calendar.' });
export type ListCalendarSlotsQuery = typeof listCalendarSlotsQuerySchema.t;

export const calendarSlotApprovalRequestedSchema = type({
  approvalRequested: type('true').configure(meta('The change was turned into an approval request; no slot was written.')),
}).configure({ description: '202 body of a slot write that needs approval.' });
export type CalendarSlotApprovalRequested = typeof calendarSlotApprovalRequestedSchema.t;
